/**
 * @fileoverview Admin Overview Page
 *
 * Purpose: Dashboard summary with key metrics and quick actions.
 * URL: /admin
 * Protection: Requires super_admin role
 *
 * Stats displayed:
 * - Total Users
 * - Total Organisations
 * - Pending Invitations
 * - Super Admins count
 *
 * Quick actions:
 * - Invite User
 * - View All Users
 * - View All Organisations
 *
 * Dependencies:
 * - Convex: admin.users.getStats, admin.organisations.getStats, admin.invitations.getStats
 */

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Button } from "@/ui/primitives/button";
import { Users, Building2, Mail, Shield, Plus, ArrowRight } from "lucide-react";
import { InviteUserDialog } from "./-ui.invite-user-dialog";

export const Route = createFileRoute("/_app/_auth/admin/_layout/")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: AdminOverview,
});

function AdminOverview() {
  const navigate = useNavigate();

  const { data: userStats } = useQuery(
    convexQuery(api.admin.users.getStats, {}),
  );
  const { data: orgStats } = useQuery(
    convexQuery(api.admin.organisations.getStats, {}),
  );
  const { data: invitationStats } = useQuery(
    convexQuery(api.admin.invitations.getStats, {}),
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Admin Overview</h1>
        <p className="text-sm text-muted-foreground">
          Platform-wide statistics and quick actions.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {userStats?.totalUsers ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Registered on the platform
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organisations</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {orgStats?.totalOrganisations ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Active organisations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Invitations
            </CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {invitationStats?.pending ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Awaiting signup</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Super Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {userStats?.superAdmins ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Morgan staff with admin access
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <InviteUserDialog>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Invite User
            </Button>
          </InviteUserDialog>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate({ to: "/admin/users" })}
          >
            View All Users
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate({ to: "/admin/organisations" })}
          >
            View All Organisations
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
